if (!customElements.get('somewhere-email-popup')) {
  customElements.define(
    'somewhere-email-popup',
    class SomewhereEmailPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-somewhere-popup-dialog]');
        this.closeButton = this.querySelector('[data-somewhere-popup-close]');
        this.statusMessage = this.querySelector('[data-somewhere-popup-status]');
        this.emailInput = this.querySelector('input[type="email"]');
        this.form = this.closest('form');
        this.formState = this.dataset.formState || 'idle';
        this.dismissKey = 'somewhere_email_popup_dismissed_at_v1';
        this.successKey = 'somewhere_email_popup_subscribed_at_v1';
        this.submissionKey = 'somewhere_email_popup_submission_v1';
        this.onScroll = this.handleScroll.bind(this);
        this.onCancel = this.handleCancel.bind(this);
        this.onBackdropClick = this.handleBackdropClick.bind(this);
        this.onSectionSelect = this.handleSectionSelect.bind(this);
        this.onSectionDeselect = this.handleSectionDeselect.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);

        if (!this.dialog || !this.closeButton) return;

        this.closeButton.addEventListener('click', () => this.close(true));
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('click', this.onBackdropClick);
        this.form?.addEventListener('submit', this.onSubmit);
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);

        const isPopupSubmission = this.hasSubmissionMarker();

        if (this.formState === 'success' && isPopupSubmission) {
          this.writeTimestamp(this.successKey);
          this.clearSubmissionMarker();
          this.open();
          return;
        }

        if (this.formState === 'error' && isPopupSubmission) {
          this.open();
          return;
        }

        if (this.dataset.designMode === 'true') return;
        if (this.isWithinCooldown(this.successKey, Number(this.dataset.successDays))) return;
        if (this.isWithinCooldown(this.dismissKey, Number(this.dataset.dismissDays))) return;

        this.openTimer = window.setTimeout(() => this.open(), Number(this.dataset.delayMs) || 10000);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.handleScroll();
      }

      disconnectedCallback() {
        this.clearTriggers();
        this.dialog?.removeEventListener('cancel', this.onCancel);
        this.dialog?.removeEventListener('click', this.onBackdropClick);
        this.form?.removeEventListener('submit', this.onSubmit);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      readTimestamp(key) {
        try {
          return Number(window.localStorage.getItem(key)) || 0;
        } catch (_error) {
          return 0;
        }
      }

      writeTimestamp(key) {
        try {
          window.localStorage.setItem(key, String(Date.now()));
        } catch (_error) {
          // The signup remains usable when storage is unavailable.
        }
      }

      hasSubmissionMarker() {
        try {
          return window.sessionStorage.getItem(this.submissionKey) === 'true';
        } catch (_error) {
          return false;
        }
      }

      clearSubmissionMarker() {
        try {
          window.sessionStorage.removeItem(this.submissionKey);
        } catch (_error) {
          // Nothing to clear when session storage is unavailable.
        }
      }

      isWithinCooldown(key, days) {
        const timestamp = this.readTimestamp(key);
        return timestamp > 0 && Date.now() - timestamp < days * 86400000;
      }

      handleScroll() {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        const percent = (window.scrollY / scrollable) * 100;
        if (percent >= (Number(this.dataset.scrollPercent) || 40)) this.open();
      }

      clearTriggers() {
        if (this.openTimer) window.clearTimeout(this.openTimer);
        window.removeEventListener('scroll', this.onScroll);
      }

      open() {
        if (!this.dialog || this.dialog.open) return;
        this.clearTriggers();
        this.previouslyFocused = document.activeElement;

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('somewhere-email-popup-open');
        window.requestAnimationFrame(() => {
          const focusTarget = this.statusMessage || this.emailInput || this.closeButton;
          focusTarget?.focus();
        });
      }

      close(persistDismissal) {
        if (!this.dialog) return;
        if (persistDismissal && this.formState !== 'success') this.writeTimestamp(this.dismissKey);
        if (this.formState === 'error') this.clearSubmissionMarker();

        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }

        document.documentElement.classList.remove('somewhere-email-popup-open');
        if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
      }

      handleCancel(event) {
        event.preventDefault();
        this.close(true);
      }

      handleBackdropClick(event) {
        if (event.target === this.dialog) this.close(true);
      }

      handleSubmit() {
        try {
          window.sessionStorage.setItem(this.submissionKey, 'true');
        } catch (_error) {
          // Server-side submission remains functional without session storage.
        }
      }

      handleSectionSelect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      }

      handleSectionDeselect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close(false);
      }
    },
  );
}
